import { isChunkLoadFailureMessage } from '@/lib/utils/chunk-load-recovery';

/**
 * Minimal Sentry error event shape for client-side filtering (avoids coupling to @sentry/core resolution).
 */
export interface SentryFilterableErrorEvent {
  type?: string;
  message?: string;
  logentry?: { message?: string };
  exception?: {
    values?: Array<{ type?: string; value?: string }>;
  };
}

/**
 * Message fragments (lowercased) that identify a transient browser-side network
 * failure — the fetch/XHR layer giving up, not our code throwing.
 *
 * Both matching paths also require the exception to be a `TypeError` (the only
 * type the fetch layer raises for these), so a wrapped or re-thrown error
 * carrying the same words stays visible.
 *
 * One list feeds both the structured-exception path and the message-only
 * fallback so the two cannot drift apart.
 */
const TRANSIENT_NETWORK_TYPE_ERROR_PHRASES = [
  // Safari / WebKit
  'load failed',
  // Chromium
  'failed to fetch',
  // Firefox: fetch could not reach the resource at all
  'networkerror when attempting to fetch resource',
  // Firefox: the fetch/XHR body stream was dropped mid-read (offline,
  // navigation abort, NS_ERROR_NET_INTERRUPT). Match the whole phrase, not an "input" token, so an app error that merely mentions
  // an input stays visible.
  'error in input stream'
] as const;

function containsTransientNetworkPhrase(text: string): boolean {
  return TRANSIENT_NETWORK_TYPE_ERROR_PHRASES.some((phrase) =>
    text.includes(phrase)
  );
}

/**
 * Transient browser-side network failures that are not actionable in Sentry:
 * offline users, ad blockers, flaky connections, Safari WebKit "Load failed",
 * Firefox "Error in input stream" (dropped fetch/XHR body).
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#checking_that_the_fetch_was_successful
 */
export function isTransientClientNetworkException(
  event: SentryFilterableErrorEvent
): boolean {
  const first = event.exception?.values?.[0];
  const type = (first?.type ?? '').toLowerCase();
  const value = (first?.value ?? '').toLowerCase();

  // A named chunk/module load failure is actionable, not noise: it means the
  // user's tab is running a bundle the server no longer serves. Never filter it.
  if (
    isChunkLoadFailureMessage(`${first?.type ?? ''}: ${first?.value ?? ''}`) ||
    isChunkLoadFailureMessage(event.message ?? event.logentry?.message ?? '')
  ) {
    return false;
  }

  if (type === 'typeerror' && containsTransientNetworkPhrase(value)) {
    return true;
  }

  // Some events arrive with no structured exception (message-only capture). The
  // type then has to come from the message text itself.
  const fallback = (
    event.message ??
    event.logentry?.message ??
    ''
  ).toLowerCase();
  if (
    fallback.includes('typeerror') &&
    containsTransientNetworkPhrase(fallback)
  ) {
    return true;
  }

  return false;
}

/**
 * A `NotFoundError` DOMException raised because React tried to remove or insert
 * a node that is no longer where it left it.
 *
 * Named for what is *observable*, not for a cause: the usual source is a
 * browser extension (Google Translate, Grammarly, password managers) mutating
 * the DOM outside React's tree, but nothing in the event proves that. The
 * predicate stays narrow rather than filtering the whole error class.
 *
 * Narrow means: the exception must actually *be* a `NotFoundError` (its Sentry
 * type, or a `NotFoundError:` prefix on the value when the error was re-thrown
 * and wrapped) — a message that merely mentions the name elsewhere does not
 * qualify — and it must name one of the two detached-node operations. React
 * hydration mismatches and ordinary render errors are a different type and
 * stay visible.
 *
 * @see https://github.com/facebook/react/issues/11538
 */
export function isDetachedNodeDomException(
  event: SentryFilterableErrorEvent
): boolean {
  const first = event.exception?.values?.[0];
  const type = first?.type ?? '';
  const value = first?.value ?? '';

  const isNotFoundError =
    type === 'NotFoundError' || /^\s*NotFoundError\b/.test(value);
  if (!isNotFoundError) return false;

  // Chrome: "Failed to execute 'removeChild' on 'Node': The node to be removed
  //          is not a child of this node."
  // Firefox: "Node.removeChild: The node to be removed is not a child of this node"
  // Safari: "The node to be removed is not a child of this node."
  const message = value.toLowerCase();
  return (
    message.includes("failed to execute 'removechild'") ||
    message.includes("failed to execute 'insertbefore'") ||
    message.includes('the node to be removed is not a child') ||
    message.includes('the node before which the new node is to be inserted')
  );
}

/**
 * Next.js throws this when a POST carries a Server Action ID that is not in
 * the current build (stale client after a deploy/rebuild, or a scanner
 * posting a plausible-looking ID). Official message:
 * https://nextjs.org/docs/messages/failed-to-find-server-action
 *
 * Narrow: both official phrases must be present, and a structured exception
 * must be `Error` (what Next.js throws). An app error that merely mentions
 * "server action" stays visible.
 */
const MISSING_SERVER_ACTION_PHRASE = 'failed to find server action';
const MISSING_SERVER_ACTION_DEPLOY_PHRASE = 'older or newer deployment';

function textLooksLikeMissingServerAction(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes(MISSING_SERVER_ACTION_PHRASE) &&
    lower.includes(MISSING_SERVER_ACTION_DEPLOY_PHRASE)
  );
}

export function isMissingServerActionException(
  event: SentryFilterableErrorEvent
): boolean {
  const first = event.exception?.values?.[0];
  if (first) {
    const type = first.type ?? '';
    if (type && type !== 'Error') return false;
    return textLooksLikeMissingServerAction(first.value ?? '');
  }

  return textLooksLikeMissingServerAction(
    event.message ?? event.logentry?.message ?? ''
  );
}

/** Same classification for raw throws (Next.js `onRequestError`). */
export function isMissingServerActionError(error: unknown): boolean {
  if (error instanceof Error) {
    return textLooksLikeMissingServerAction(`${error.name}: ${error.message}`);
  }
  if (typeof error === 'string') {
    return textLooksLikeMissingServerAction(error);
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return textLooksLikeMissingServerAction(message);
    }
  }
  return false;
}

/**
 * Office.js / Outlook add-in COM rejection.
 *
 * Sentry captures these as a non-Error `unhandledrejection` with no stack:
 * `Object Not Found Matching Id:N, MethodName:update, ParamCount:4`. The
 * phrase is Office.js talking to a disposed host object — not thrown by this app.
 *
 * Narrow means: the exact three-field Office.js signature must be present.
 * A generic "object not found", a 404, or any other UnhandledRejection stays
 * visible.
 *
 * @see https://github.com/OfficeDev/office-js/issues/152
 */
const OFFICE_JS_OBJECT_NOT_FOUND =
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;

export function isOfficeJsObjectNotFoundRejection(
  event: SentryFilterableErrorEvent
): boolean {
  const first = event.exception?.values?.[0];
  const candidates = [
    first?.value ?? '',
    event.message ?? '',
    event.logentry?.message ?? ''
  ];
  return candidates.some((text) => OFFICE_JS_OBJECT_NOT_FOUND.test(text));
}
